import { useTranslations } from 'next-intl';
import { deliveryCopyKeys, sectionBadges, type Section } from '@/lib/product-sections';
import { TrustBadges } from './trust-badges';

interface DeliveryInfoProps {
  /** Which part of the shop this product belongs to; decides the copy. */
  section: Section;
  /** `th_skickas_ej` — the product is never shipped. */
  pickupOnly?: boolean;
  /** `th_licenspliktig` — handover requires a valid weapon licence. */
  requiresLicense?: boolean;
  /**
   * `status_text` — the ERP will not sell this online at all. Outranks
   * `pickupOnly`: see ProductConditions in lib/product-sections.ts.
   */
  notSellableOnline?: boolean;
}

/**
 * The delivery accordion body — "Leverans & licens" for a licence-bound
 * product, plain "Leverans" otherwise (p.8).
 *
 * This is where the trust badges live. The redesign clears them out of the buy
 * box so the licence notice can carry it alone; what changed with Torsbo's
 * feedback is that they are no longer a fixed set of three. Free shipping and a
 * 30-day open purchase are real promises for a jacket and false ones for a
 * rifle (p.3), so both the paragraph and the badges come from the section — and
 * from the licence flag, which overrides it. See lib/product-sections.ts.
 */
export function DeliveryInfo({
  section,
  pickupOnly = false,
  requiresLicense = false,
  notSellableOnline = false,
}: DeliveryInfoProps) {
  const t = useTranslations('product');

  // Several conditions can hold at once — ammunition is licence-bound and
  // never shipped — so this is a list. Pickup-only used to replace the section's copy
  // outright, which silently dropped the licence terms from 411 of 418
  // ammunition pages. See lib/product-sections.ts.
  const copyKeys = deliveryCopyKeys(section, {
    licensed: requiresLicense,
    pickupOnly,
    notSellableOnline,
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2.5">
        {copyKeys.map((key) => (
          <p key={key} className="max-w-[62ch] text-[14.5px] leading-[1.7] text-foreground/65">
            {t(key)}
          </p>
        ))}
      </div>

      <TrustBadges
        badges={sectionBadges(section, {
          licensed: requiresLicense,
          pickupOnly,
          notSellableOnline,
        })}
      />
    </div>
  );
}
